<script>
    import { link, push } from "svelte-spa-router";
    import { getAccessToken } from "../auth";
    import Header from "../components/Header.svelte";
    import { environment, isAuthenticated, showDebug, hostname, stack } from "../stores";
    import { getOrganisationDisplayName, getStorageCapacityApi } from "../utils";
    import UserAdmin from "../components/UserAdmin.svelte";
    import TeamAdmin from "../components/TeamAdmin.svelte";
    import { toast } from "@zerodevx/svelte-toast";
    import { toastThemeError } from "../toasts";

    export let params = {};

    let userServiceURL = "https://dev-user-service.livelyforest-c696615c.westeurope.azurecontainerapps.io";
    if ($environment === "prod") {
        userServiceURL = "https://ext-prod-user-service.livelyforest-c696615c.westeurope.azurecontainerapps.io";
    }

    let organizations = [];
    let authToken;
    let selectedOrg = null;
    let tab;

    $: {
        if ($isAuthenticated) {
            initializeWithData();
        }
    }

    async function initializeWithData() {
        authToken = await getAccessToken();
        const response = await fetchOrganizationInfo();
        const data = await response.json();
        if(response.status === 200) {
            organizations = data.sort((a, b) => b.name - a.name);
            selectedOrg = getOrganizationFromParam();
            tab = getTabFromParam();
        }
        else {
            toast(data.message, toastThemeError);
        }
    }

    function getOrganizationFromParam() {
        if (params?.orgId?.length === 24) {
            const paramOrganization = organizations.find((org) => org._id === params.orgId);
            if (paramOrganization) {
                return paramOrganization;
            }
        }
        return organizations[0];
    }

    function getTabFromParam() {
        if(params?.tab === "user" || params?.tab === "team") {
            return params.tab;
        }
        return "user";
    }



    async function fetchOrganizationInfo() {
        try {
            const response = await fetch(userServiceURL + "/organization", {
                method: "GET",
                headers: {
                    Authorization: "Bearer " + authToken,
                    Accept: "*/*",
                },
            });
            return response;
        }
        catch {
            toast.push("Failed to fetch organization info", toastThemeError);
        }
    }

    async function newOrganization() {
        const tabParam = params?.tab ? params.tab : "";
        push(`/admin/${selectedOrg._id}/${tabParam}`);
    }

    let usedStorage;
    let checkingUsedStorage = false;
    async function onUsedStorage() {
        checkingUsedStorage = true;
        try {
            const response = await getStorageCapacityApi($hostname, $isAuthenticated, $stack);
            checkingUsedStorage = false;
            usedStorage = response;
        } catch {
            toast.push("Failed to fetch storage info", toastThemeError);
            checkingUsedStorage = false;
            usedStorage = {message: 'FETCH FAILED'};
        }
    }
</script>

<main-window>
    <Header />
    <nav>
        <a href="/" use:link>Home</a>
    </nav>
    <admin-container>
        <admin-header>
            <nav>
                {#if organizations.length > 1}
                    <select bind:value={selectedOrg} on:change={() => newOrganization()}>
                        {#each organizations as org}
                            <option value={org}>
                                {getOrganisationDisplayName(org.name)}
                            </option>
                        {/each}
                    </select>
                {/if}
                <a class={tab === "user" ? "active" :""} href="/admin/{selectedOrg?._id}/user" use:link on:click={() => tab = "user"}>Users</a>
                <a class={tab === "team" ? "active" :""} href="/admin/{selectedOrg?._id}/team" use:link on:click={() => tab = "team"}>Teams</a>
            </nav>
        </admin-header>
        <admin-body>
            {#if selectedOrg}
                {#if tab === "user"}
                    <UserAdmin organization={selectedOrg} />
                {/if}
                {#if tab === "team"}
                    <TeamAdmin organization={selectedOrg} />
                {/if}
            {/if}
        </admin-body>
        <admin-experimental>
            {#if selectedOrg && $showDebug}
                <button type="button" on:click={onUsedStorage}>Check used storage</button>
                {#if checkingUsedStorage}
                    <span class="experimental-text">Fetching storage stats...</span>
                {:else}
                    {#if usedStorage}
                        {#if usedStorage?.stack == $stack}
                            <span class={usedStorage?.used > 10995116277760 ? "experimental-text-highlight" : "experimental-text"}>{(usedStorage?.used / 1024 / 1024 / 1024).toFixed()} GiB</span>
                            <span class="experimental-text">{usedStorage?.count} objects</span>
                        {:else}
                            <span class="experimental-text">Could not fetch storage stats. Try again later.</span>
                        {/if}
                    {/if}
                {/if}
            {/if}
        </admin-experimental>
    </admin-container>
</main-window>

<style>
    nav {
        display: flex;
        margin-bottom: 20px;
        align-items: baseline;
        gap: 1em;
    }

    .active {
        text-decoration: underline;
    }

    admin-container {
        display: block;
        padding: 0px 30px;
        max-width: 1400px;
    }

    admin-experimental {
        display: block;
        margin: 8px;
        text-align: left;
    }

    .experimental-text {
        margin-left: 4em;
    }

    .experimental-text-highlight {
        margin-left: 4em;
        color: red;
    }

    select {
        background-color: white;
        border: none;
    }

    select:hover {
        color: var(--var-theme-hover-background-color);
    }
</style>
